/**
 * IdeesManager — gestion de la boîte à idées (mairie / GRC).
 * Suit les idées citoyennes, change leur statut + répond, modère (masquer).
 * Greffé sur la page Référendums/Participation.
 *
 * Conforme au guide : zéro window.confirm (useConfirmDialog + {dialogElement}).
 */

import { useEffect, useState, useCallback } from 'react';
import { Button, Badge, Alert, Field } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { Idee, IdeeCategorie, IdeeStatut } from '@/lib/api';
import { Lightbulb, ThumbsUp, MessageSquare, EyeOff, Eye, AlertCircle } from 'lucide-react';

const CATEGORIE_LABEL: Record<IdeeCategorie, string> = {
  cadre_de_vie: 'Cadre de vie', voirie: 'Voirie', environnement: 'Environnement', culture: 'Culture',
  sport: 'Sport', jeunesse: 'Jeunesse', solidarite: 'Solidarité', commerce: 'Commerce', autre: 'Autre',
};
const STATUT_LABEL: Record<IdeeStatut, string> = {
  nouvelle: 'Nouvelle', a_l_etude: 'À l\'étude', retenue: 'Retenue', realisee: 'Réalisée', refusee: 'Non retenue',
};
const STATUT_VARIANT: Record<IdeeStatut, 'neutral' | 'info' | 'success' | 'warning'> = {
  nouvelle: 'neutral', a_l_etude: 'info', retenue: 'success', realisee: 'success', refusee: 'warning',
};

export function IdeesManager() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [idees, setIdees] = useState<Idee[]>([]);
  const [voirMasquees, setVoirMasquees] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [traiter, setTraiter] = useState<Idee | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setIdees((await api.grc.idees.list({ masquees: voirMasquees })).idees); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, [voirMasquees]);
  useEffect(() => { void reload(); }, [reload]);

  async function masquer(i: Idee, masquee: boolean) {
    const ok = await confirm({
      title: masquee ? 'Masquer cette idée ?' : 'Rendre visible ?',
      description: masquee ? `« ${i.titre} » ne sera plus visible des habitants.` : `« ${i.titre} » sera de nouveau visible.`,
      confirmLabel: masquee ? 'Masquer' : 'Rendre visible', danger: masquee,
    });
    if (!ok) return;
    try { await api.grc.idees.masquer(i.id, masquee); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Action impossible'); }
  }

  return (
    <div>
      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

      <div className="mb-3 flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-sm text-ink-muted">
          <Lightbulb className="h-4 w-4 text-grc" /> Idées proposées par les habitants, triées par soutien.
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
          <input type="checkbox" checked={voirMasquees} onChange={(e) => setVoirMasquees(e.target.checked)}
            className="h-4 w-4 rounded border-border text-grc focus-visible:ring-grc" />
          Voir les idées masquées
        </label>
      </div>

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : idees.length === 0 ? (
        <p className="text-sm text-ink-muted">Aucune idée pour l'instant.</p>
      ) : (
        <ul className="space-y-2">
          {idees.map((i) => (
            <li key={i.id} className={`rounded-r-lg border border-border bg-surface/50 p-3 ${i.masquee ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 font-medium text-ink">
                    <span>{i.titre}</span>
                    <Badge variant="neutral" size="sm">{CATEGORIE_LABEL[i.categorie]}</Badge>
                    <Badge variant={STATUT_VARIANT[i.statut]} size="sm">{STATUT_LABEL[i.statut]}</Badge>
                    {i.masquee && <Badge variant="warning" size="sm">Masquée</Badge>}
                  </div>
                  <p className="mt-0.5 text-sm text-ink-muted">{i.description}</p>
                  {i.reponse && <p className="mt-1 text-sm text-ink"><span className="text-ink-dim">Réponse :</span> {i.reponse}</p>}
                  <div className="mt-1 flex items-center gap-3 text-xs text-ink-dim">
                    <span className="inline-flex items-center gap-1"><ThumbsUp className="h-3 w-3" />{i.soutiens} soutien{i.soutiens > 1 ? 's' : ''}</span>
                    <span>Par {i.auteurNom ?? 'un habitant'}</span>
                  </div>
                </div>
                <div className="flex flex-shrink-0 gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setTraiter(i)} title="Répondre / changer le statut"><MessageSquare className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => void masquer(i, !i.masquee)} title={i.masquee ? 'Rendre visible' : 'Masquer'}>
                    {i.masquee ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {traiter && (
        <TraiterDialog idee={traiter} onClose={() => setTraiter(null)}
          onSaved={async () => { setTraiter(null); await reload(); }} />
      )}
      {dialogElement}
    </div>
  );
}

function TraiterDialog({ idee, onClose, onSaved }: { idee: Idee; onClose: () => void; onSaved: () => void }) {
  const [statut, setStatut] = useState<IdeeStatut>(idee.statut);
  const [reponse, setReponse] = useState(idee.reponse ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const selectCls = 'w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  async function save() {
    setBusy(true); setErr(null);
    try { await api.grc.idees.traiter(idee.id, statut, reponse.trim() || undefined); onSaved(); }
    catch (e) { setErr(e instanceof ApiException ? e.message : 'Enregistrement impossible'); setBusy(false); }
  }

  return (
    <Dialog open onClose={onClose} title="Répondre à l'idée">
      <div className="space-y-3">
        {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
        <div className="rounded-r-lg border border-border bg-surface/50 p-3">
          <div className="font-medium text-ink">{idee.titre}</div>
          <p className="mt-0.5 text-sm text-ink-muted">{idee.description}</p>
        </div>
        <Field label="Statut" htmlFor="idee-stat">
          <select id="idee-stat" className={selectCls} value={statut} onChange={(e) => setStatut(e.target.value as IdeeStatut)}>
            {Object.entries(STATUT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Réponse publique (optionnel — notifiée à l'auteur)" htmlFor="idee-rep">
          <textarea id="idee-rep" rows={3}
            className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
            value={reponse} onChange={(e) => setReponse(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </div>
      </div>
    </Dialog>
  );
}
